import { createTheme } from '@mui/material/styles';

export const liquidGlassTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#E72F0D',
      light: '#FF5A3D',
      dark: '#C42009',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0D0D0D',
      light: '#333333',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F2F4F8',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0D0D0D',
      secondary: '#7A7A7A',
    },
    divider: '#E8E8E8',
    success: {
      main: '#22C55E',
    },
    warning: {
      main: '#F59E0B',
    },
    error: {
      main: '#E72F0D',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
    h1: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 600,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *:focus, *:focus-visible, *:focus-within': {
          outline: 'none !important',
          outlineWidth: '0 !important',
          outlineOffset: '0 !important',
        },
        'input, textarea, select, button, [role="button"], [tabindex]': {
          outline: 'none !important',
        },
        'input:focus, textarea:focus, select:focus, button:focus': {
          outline: 'none !important',
        },
        body: {
          backgroundColor: '#F2F4F8',
          scrollbarColor: "rgba(158, 158, 158, 0.5) #f5f5f5",
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            width: 8,
            height: 8,
          },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: "rgba(158, 158, 158, 0.4)",
            border: "2px solid transparent",
            minHeight: 24,
          },
          "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(158, 158, 158, 0.6)",
          },
          "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
            borderRadius: 8,
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 2px 10px rgba(15,23,42,0.08)',
          border: '1px solid #E8E8E8',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          border: '1px solid #E8E8E8',
          boxShadow: '0 2px 10px rgba(15,23,42,0.08)',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: '#E72F0D',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#C42009',
          },
        },
        containedSecondary: {
          backgroundColor: '#0D0D0D',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#333333',
          },
        },
        outlined: {
          borderColor: '#E8E8E8',
          color: '#0D0D0D',
          '&:hover': {
            borderColor: '#B0B0B0',
            backgroundColor: '#FAFAFA',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#FFFFFF',
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: '#E8E8E8',
            },
            '&:hover fieldset': {
              borderColor: '#B0B0B0',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#E72F0D',
              borderWidth: 1,
            },
          },
          '& .MuiInputBase-input': {
            fontSize: '14px',
            color: '#0D0D0D',
            '&::placeholder': {
              color: '#D0D0D0',
              opacity: 1,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:focus, &:focus-visible, &:focus-within': {
            outline: 'none !important',
          },
          '& input:focus, & textarea:focus': {
            outline: 'none !important',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&:focus, &:focus-visible, &:focus-within': {
            outline: 'none !important',
          },
          '& input:focus, & textarea:focus': {
            outline: 'none !important',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#FFFFFF',
          '&:focus, &:focus-visible': {
            outline: 'none !important',
          },
        },
        select: {
          '&:focus': {
            outline: 'none !important',
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: '1px solid #E8E8E8',
          boxShadow: '0 32px 64px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
        standardWarning: {
          backgroundColor: '#FFF8F0',
          border: '1px solid #FDECD3',
          color: '#7A7A7A',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E8E8E8',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid #E8E8E8',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0D0D0D',
          color: '#FFFFFF',
          borderRadius: 8,
          fontSize: '13px',
          padding: '8px 12px',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: '1px solid #E8E8E8',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          marginTop: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: '#F5F5F5',
          },
          '&.Mui-selected': {
            backgroundColor: '#F5F5F5',
            '&:hover': {
              backgroundColor: '#EEEEEE',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '14px',
        },
      },
    },
  },
});
